import React, { startTransition, useCallback, useEffect, useState, useMemo } from 'react';
import SearchPageSidebarSection from './SearchPageSidebarSection';
import SearchPageSidebarTagGroup from './SearchPageSidebarTagGroup';
import { array, bool, func, number, object, oneOfType, string } from 'prop-types';
import PriceRange from './PriceRange';
import constants from '../constants.json' assert { type: 'json' };

/**
 * CONFIGURATION
 */


/* Limit Values */
export const median = (arr = []) => {
    const sortedArr = [...arr]
        .filter((n) => !isNaN(parseInt(n)))
        .map((n) => parseInt(n));
    const half = Math.floor(sortedArr.length / 2);
    return sortedArr.length % 2 === 0 ?
        sortedArr[half] : (sortedArr[half - 1] + sortedArr[half]) / 2;
};
export const limitSet = constants.SEARCH_PAGE.RESULT_LIMITS;

/**
 * @function isValidPrice
 * @param {*} price
 * @param {*} comparePrice
 * @param {*} allowZero
 * @returns
 */
const isValidPrice = (price, comparePrice = null, allowZero = false) => {
    if (price === '' || price === undefined || price === null) {
        return null;
    }
    if (isNaN(parseFloat(price))) {
        return false;
    }
    const defaultValue = allowZero ? -1 : 0;
    return parseFloat(price) > (isValidPrice(comparePrice) ? parseFloat(comparePrice) : defaultValue);
};

export default function SearchPageSidebar({ isMobile, searchQuery, setSearchQuery, vendorList, tagPrefixes, tagList, searchLoading, productCount, productMaxPrice }) {
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [selectedVendor, setSelectedVendor] = useState('');
    const handlePriceChange = useCallback((minPrice, maxPrice) => {
        const minValid = isValidPrice(minPrice, null, true);
        const maxValid = isValidPrice(maxPrice, minPrice, false);
        if (minValid === false
            && maxValid === false) {
            return;
        }
        setSearchQuery({
            ...searchQuery,
            minPrice: minValid ? parseFloat(minPrice).toFixed(2) : '',
            maxPrice: maxValid ? parseFloat(maxPrice).toFixed(2) : '',
        });
    }, []);
    useEffect(() => {
        startTransition(() => {
            setMinPrice(searchQuery.minPrice ?? '');
            setMaxPrice(searchQuery.maxPrice ?? '');
        });
    }, [searchQuery.minPrice, searchQuery.maxPrice]);
    const searchVendor = useMemo(() => searchQuery?.v ?? '', [searchQuery]);
    useEffect(() => {
        startTransition(() => {
            setSelectedVendor(searchVendor);
        });
    }, [searchVendor]);

    const handleVendorChange = (newVendorValue) => {
        setSelectedVendor(newVendorValue);
        setSearchQuery({
            ...searchQuery,
            v: newVendorValue ?? '',
        });
    };

    const onVendorChange = useCallback((e) => {
        handleVendorChange(e.target.value);
    }, []);
    return (<div
        className={'search-page--sidebar'}>
        <SearchPageSidebarSection
            isMobile={isMobile}
            headingText={'Price'}>
            <PriceRange
                minPrice={0}
                maxPrice={productMaxPrice}
                queryMinPrice={minPrice}
                queryMaxPrice={maxPrice}
                handlePriceChange={handlePriceChange}
            />
        </SearchPageSidebarSection>
        {tagPrefixes.length > 0 && tagPrefixes.map((tagPrefix) => (<SearchPageSidebarSection
            isMobile={isMobile}
            headingText={tagPrefix.tagPrefixValue}
            key={tagPrefix.tagPrefixValue}
            itemCount={tagList.filter((tag) => tag.tagPrefix === tagPrefix.tagPrefixValue).length}>
            <SearchPageSidebarTagGroup
                isMobile={isMobile}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                tagPrefix={tagPrefix.tagPrefixValue}
                tagList={tagList.filter((tag) => tag.tagPrefix === tagPrefix.tagPrefixValue)}
                productCount={productCount} />
        </SearchPageSidebarSection>))}
        <SearchPageSidebarSection
            isMobile={isMobile}
        >
            <div
                className={'d-flex align-items-center gap-2 text-nowrap my-2'}>
                <span>Vendors:</span>
                <select
                    className={'form-select form-select--small search-sidebar--input'}
                    value={selectedVendor}
                    onChange={onVendorChange}
                >
                    <option
                        value={''}
                    >
                        All
                    </option>
                    {(vendorList.length > 0 && (searchLoading === true || productCount > 0)) && vendorList
                        .filter((vendor) => {
                            if (productCount > 0) {
                                return true;
                            }
                            return selectedVendor !== '' && vendor.vendor === selectedVendor;
                        })
                        .map((vendor) => (
                            <option
                                key={vendor.vendor}
                                value={vendor.vendor}
                            >
                                {vendor.vendor} ({productCount > 0 ? vendor.vendorCount : 0})
                            </option>
                        ))}
                </select>
            </div>
        </SearchPageSidebarSection>
    </div >);
}

SearchPageSidebar.propTypes = {
    isMobile: bool,
    searchQuery: object,
    setSearchQuery: func,
    vendorList: array,
    tagPrefixes: array,
    tagList: array,
    searchLoading: bool,
    productCount: oneOfType([number, string]),
    productMaxPrice: number,
};